import type {
  CancelStaffWeeklyOffDayRequestInput,
  CreateStaffWeeklyOffDayRequestInput,
  ListStaffPaymentsResponse,
  ListStaffTasksResponse,
  ListStaffWeeklyOffDayRequestsResponse,
  ListStaffWorkSessionsResponse,
  OpsMe as BackendOpsMe,
  OpsHeartbeatResponse,
  OpsSummary,
  StaffApprovedOffDayMetadata,
  StaffTaskDetail,
  StaffTaskListItem,
  StaffWeeklyOffDayRequest,
  StaffWeeklyOffDayRequestStatus,
  StaffWorkDayKind,
  StaffWorkSession,
  StaffWorkSessionStatus,
  StaffWorkSessionTaskSegment,
} from '@mbbe/types';
import { opsApiFetch } from './opsApiClient';

export type OpsTaskStatus = 'assigned' | 'in_progress' | 'on_hold' | 'review' | 'manager_request' | 'revision' | 'done' | 'cancelled' | 'archive';
export type OpsTaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type OpsPaymentStatus = 'pending' | 'approved' | 'paid' | 'rejected';
export type OpsTimerStatus = 'idle' | 'running';
export type OpsWeeklyOffDayRequestStatus = StaffWeeklyOffDayRequestStatus;
export type OpsWorkDayKind = StaffWorkDayKind;
export type OpsWorkSessionStatus = StaffWorkSessionStatus;
export type OpsApprovedOffDay = StaffApprovedOffDayMetadata;
export type OpsWeeklyOffDayRequest = StaffWeeklyOffDayRequest;
export type CreateOpsWeeklyOffDayRequestInput = Omit<CreateStaffWeeklyOffDayRequestInput, 'requestedDate' | 'requestedDates'> & {
  requestedDate?: string;
  requestedDates: string[];
};
export type CancelOpsWeeklyOffDayRequestInput = CancelStaffWeeklyOffDayRequestInput;

export interface OpsMe {
  id: string;
  firebaseUid?: string;
  email: string;
  displayName?: string | null;
  honorific?: 'bey' | 'hanim' | null;
  role?: string;
  title?: string | null;
  avatarUrl?: string | null;
  hourlyRate?: number | null;
  currency?: string | null;
  active?: boolean;
}

export interface OpsContinuationVoiceMeta {
  staffId: string;
  text: string;
  voice: string;
  model: string;
  cacheKey: string;
}

export interface OpsContinuationVoice extends OpsContinuationVoiceMeta {
  mimeType: string;
  audioBase64: string;
}

export interface OpsActiveTimer {
  id: string;
  taskId: string;
  taskTitle: string;
  projectName?: string | null;
  startedAt: string;
  nextCheckAt?: string | null;
  note?: string | null;
  status: OpsTimerStatus;
  segments?: OpsWorkSegment[];
  workDayKind?: OpsWorkDayKind;
  approvedOffDay?: OpsApprovedOffDay | null;
}

export interface OpsWorkSegment {
  id: string;
  taskId: string;
  taskTitle: string;
  startedAt: string;
  endedAt?: string | null;
  minutes: number;
  amount?: number | null;
  currency?: string | null;
  workDayKind?: OpsWorkDayKind;
  approvedOffDay?: OpsApprovedOffDay | null;
}

export interface OpsContinuationPrompt {
  id: string;
  workSessionId: string;
  promptedAt: string;
  deadlineAt: string;
  remainingSeconds: number;
}

export interface OpsHeartbeat {
  serverNow: string;
  activeTimer: OpsActiveTimer | null;
  continuationPrompt: OpsContinuationPrompt | null;
  autoFinishedLog: OpsWorkLog | null;
}

/** The one timezone every Ops reporting period (today / week / month) is evaluated in. */
export const OPS_REPORTING_TIME_ZONE = 'Europe/Istanbul';

export type OpsReportingPeriodKey = 'today' | 'week' | 'month';

/** A half-open reporting interval [from, to) in absolute instants. */
export interface OpsReportingPeriod {
  key: OpsReportingPeriodKey;
  /** Inclusive start instant (ISO). */
  from: string;
  /** Exclusive end instant (ISO). The summary request sends `to - 1ms` because the API bound is inclusive. */
  to: string;
}

/**
 * All-time money position, derived only from server fields: `earned` is the pay for completed work
 * sessions (`earnedMinor`), `paid` the recorded staff payments (`paidMinor`) and `balance` their
 * difference (`balanceMinor`). The server has no separate "approved" amount, so none is invented.
 */
export interface OpsDashboardBalance {
  currency: string;
  earned: number;
  paid: number;
  balance: number;
}

/** Totals for one explicit reporting period; `paid` is filtered by the payment's own `paidAt`. */
export interface OpsPeriodSummary {
  period: OpsReportingPeriod;
  timeZone: string;
  /** The instant the period was derived from, captured once per read. */
  referenceAt: string;
  minutes: number;
  earned: number;
  paid: number;
  currency: string;
}

export interface OpsDashboardTimeSummary {
  referenceAt: string;
  timeZone: string;
  today: OpsPeriodSummary;
  week: OpsPeriodSummary;
}

/** Task totals of the full server-side filtered sets (archived and cancelled tasks belong to neither). */
export interface OpsDashboardTaskCounts {
  open: number;
  completed: number;
}

export interface OpsTaskListItem {
  id: string;
  title: string;
  description?: string | null;
  projectName?: string | null;
  customerName?: string | null;
  status: OpsTaskStatus;
  priority: OpsTaskPriority;
  assignedAt?: string | null;
  updatedAt?: string | null;
  dueAt?: string | null;
  estimatedMinutes?: number | null;
  spentMinutes: number;
  billableAmount?: number | null;
  currency?: string | null;
  activeTimerId?: string | null;
  activeStaffLabel?: string | null;
  activeStartedAt?: string | null;
  cancelRequestedAt?: string | null;
  cancelRequestReason?: string | null;
  reviewState?: 'waiting_admin' | 'waiting_staff' | 'closed' | 'none';
  reviewStateLabel?: string;
  reviewRound?: number;
  hasUnreadUpdate?: boolean;
  latestUpdateAt?: string | null;
  latestUpdateLabel?: string | null;
  latestUpdateActorLabel?: string | null;
}

export interface OpsTaskComment {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
}

export interface OpsTaskReviewEvent {
  id: string;
  taskId: string;
  roundNo: number;
  eventType: 'staff_submit_review' | 'staff_manager_request' | 'admin_status_change';
  actorUserId: string | null;
  actorLabel: string | null;
  actorRole: 'staff' | 'admin';
  message: string | null;
  fromStatus: OpsTaskStatus | null;
  toStatus: OpsTaskStatus | null;
  createdAt: string;
}

export interface OpsTaskPersonalNote {
  id: string;
  taskId: string;
  userId: string;
  authorLabel: string | null;
  body: string;
  editable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OpsWorkLog {
  id: string;
  taskId: string;
  startedAt: string;
  finishedAt?: string | null;
  minutes: number;
  note?: string | null;
  amount?: number | null;
  /** Server state of the session: a 'void' session is an admin correction and earns nothing, which is not a real zero. */
  sessionStatus: OpsWorkSessionStatus;
  currency?: string | null;
  workDayKind?: OpsWorkDayKind;
  approvedOffDay?: OpsApprovedOffDay | null;
}

export interface OpsTaskDetail extends OpsTaskListItem {
  acceptanceCriteria?: string[] | null;
  attachments?: Array<{ id: string; filename: string; url: string; size?: number | null }> | null;
  comments: OpsTaskComment[];
  workLogs: OpsWorkLog[];
  reviewTimeline: OpsTaskReviewEvent[];
  personalNotes: OpsTaskPersonalNote[];
}

export interface OpsEarningsSummary {
  currency: string;
  pending: number;
  approved: number;
  paid: number;
  totalMinutes: number;
  regularMinutes: number;
  approvedOffDayMinutes: number;
  regularEarnings: number;
  approvedOffDayEarnings: number;
  periodLabel?: string | null;
}

/**
 * A recorded payment. The backend attributes a payment to the staff member and a payment date only: it
 * carries no covered period and no worked duration, so neither exists here and none is derived.
 */
export interface OpsPayment {
  id: string;
  amount: number;
  currency: string;
  method: string | null;
  issuedAt: string | null;
  paidAt: string;
  note: string | null;
}

/**
 * One server page of a history list. `fetched` is the number of rows the server returned for this page
 * (before any client-side exclusion), so the next offset never depends on how many rows are displayed.
 */
export interface OpsHistoryPage<T> {
  items: T[];
  offset: number;
  limit: number;
  total: number;
  fetched: number;
}

/** `open` = every task that is not done, cancelled or archived; `all` = every non-archived task. */
export type OpsTaskListStatus = OpsTaskStatus | 'open' | 'all';
export type OpsTaskListSort = 'recommended' | 'due' | 'priority' | 'updated';

export interface ListOpsTasksParams {
  status?: OpsTaskListStatus;
  q?: string;
  sort?: OpsTaskListSort;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
  /**
   * Default true: also reads the running session to mark `activeTimerId` when the task row does not
   * carry it. Callers that already hold the authoritative timer (the timer picker) pass false to
   * avoid a second request per page.
   */
  withActiveSession?: boolean;
}

export interface ListOpsTasksResponse {
  items: OpsTaskListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface OpsOffDayWeekStatus {
  weekStartDate: string;
  weekEndDate: string;
  weeklyOffDayCount: number;
  defaultOffDayDates: string[];
  approvedOffDayDates: string[];
  effectiveOffDayDates: string[];
  pendingRequest: OpsWeeklyOffDayRequest | null;
  requests: OpsWeeklyOffDayRequest[];
}

export interface OpsOffDaysStatus {
  today: string;
  timeZone: string;
  currentWeek: OpsOffDayWeekStatus;
  nextWeek: OpsOffDayWeekStatus;
}

export interface ListOpsWeeklyOffDayRequestsParams {
  status?: OpsWeeklyOffDayRequestStatus | 'all';
  limit?: number;
  offset?: number;
}

export type ListOpsWeeklyOffDayRequestsResponse = ListStaffWeeklyOffDayRequestsResponse;

function money(minor?: number | null): number {
  return (minor ?? 0) / 100;
}

function minutes(seconds?: number | null): number {
  return Math.round((seconds ?? 0) / 60);
}

function summaryPath(period: OpsReportingPeriod): string {
  // The API's `to` bound is inclusive, so the half-open period end is sent one millisecond earlier.
  const qs = new URLSearchParams({ from: period.from, to: new Date(Date.parse(period.to) - 1).toISOString() });
  return `/ops/summary?${qs}`;
}

function zonedDateParts(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: read('year'), month: read('month'), day: read('day') };
}

/** Offset of `timeZone` from UTC at `instant` in ms (positive east of Greenwich). */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const wallClockAsUtc = Date.UTC(read('year'), read('month') - 1, read('day'), read('hour'), read('minute'), read('second'));
  return wallClockAsUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Local midnight of a (possibly overflowing) calendar date in `timeZone`, as an absolute instant. */
function zonedMidnight(year: number, month: number, day: number, timeZone: string): Date {
  const wallClockAsUtc = Date.UTC(year, month - 1, day);
  const firstGuess = wallClockAsUtc - zoneOffsetMs(new Date(wallClockAsUtc), timeZone);
  return new Date(wallClockAsUtc - zoneOffsetMs(new Date(firstGuess), timeZone));
}

/**
 * Today, ISO week (Monday start) and calendar month around `reference`, all evaluated in one
 * reporting time zone regardless of the browser's own zone. Pure: the caller captures `reference` once.
 */
export function getOpsReportingPeriods(
  reference: Date,
  timeZone: string = OPS_REPORTING_TIME_ZONE
): Record<OpsReportingPeriodKey, OpsReportingPeriod> {
  const { year, month, day } = zonedDateParts(reference, timeZone);
  const dayOfWeek = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  const period = (key: OpsReportingPeriodKey, from: Date, to: Date): OpsReportingPeriod => ({
    key,
    from: from.toISOString(),
    to: to.toISOString(),
  });
  return {
    today: period('today', zonedMidnight(year, month, day, timeZone), zonedMidnight(year, month, day + 1, timeZone)),
    week: period('week', zonedMidnight(year, month, day - daysSinceMonday, timeZone), zonedMidnight(year, month, day - daysSinceMonday + 7, timeZone)),
    month: period('month', zonedMidnight(year, month, 1, timeZone), zonedMidnight(year, month + 1, 1, timeZone)),
  };
}

function mapTaskStatus(status: StaffTaskListItem['status']): OpsTaskStatus {
  return status;
}

function apiTaskStatus(status?: OpsTaskListStatus): string | undefined {
  if (!status || status === 'all') return undefined;
  return status;
}

function mapSegment(segment: StaffWorkSessionTaskSegment): OpsWorkSegment {
  return {
    id: segment.id,
    taskId: segment.taskId ?? '',
    taskTitle: segment.taskTitle || 'Genel çalışma',
    startedAt: segment.startedAt,
    endedAt: segment.endedAt,
    minutes: minutes(segment.durationSeconds),
    amount: money(segment.amountMinor),
    currency: segment.currency,
    workDayKind: segment.workDayKind,
    approvedOffDay: segment.approvedOffDay,
  };
}

function mapTimer(session: StaffWorkSession | null): OpsActiveTimer | null {
  if (!session) return null;
  return {
    id: session.id,
    taskId: session.taskId ?? '',
    taskTitle: session.taskTitle || session.subject,
    projectName: null,
    startedAt: session.startedAt,
    nextCheckAt: session.nextCheckAt,
    note: session.subject,
    status: 'running',
    segments: session.segments?.map(mapSegment),
    workDayKind: session.workDayKind,
    approvedOffDay: session.approvedOffDay,
  };
}

function mapTask(task: StaffTaskListItem, activeSession?: StaffWorkSession | null): OpsTaskListItem {
  return {
    id: task.id,
    title: task.title,
    description: task.description,
    projectName: task.customerProjectName,
    customerName: null,
    status: mapTaskStatus(task.status),
    priority: task.priority,
    assignedAt: task.createdAt,
    updatedAt: task.updatedAt,
    dueAt: task.dueAt,
    estimatedMinutes: task.estimatedMinutes,
    spentMinutes: minutes(task.durationSeconds),
    billableAmount: money(task.amountMinor),
    currency: task.currency,
    activeTimerId: task.activeSessionId ?? (activeSession?.taskId === task.id ? activeSession.id : null),
    activeStaffLabel: task.activeStaffLabel,
    activeStartedAt: task.activeStartedAt,
    cancelRequestedAt: task.cancelRequestedAt,
    cancelRequestReason: task.cancelRequestReason,
    reviewState: task.reviewState,
    reviewStateLabel: task.reviewStateLabel,
    reviewRound: task.reviewRound,
    hasUnreadUpdate: task.hasUnreadUpdate,
    latestUpdateAt: task.latestUpdateAt,
    latestUpdateLabel: task.latestUpdateLabel,
    latestUpdateActorLabel: task.latestUpdateActorLabel,
  };
}

function mapWorkLog(session: StaffWorkSession): OpsWorkLog {
  return {
    id: session.id,
    taskId: session.taskId ?? '',
    startedAt: session.startedAt,
    finishedAt: session.endedAt,
    minutes: minutes(session.durationSeconds),
    note: session.subject,
    amount: money(session.amountMinor),
    sessionStatus: session.status,
    currency: session.currency,
    workDayKind: session.workDayKind,
    approvedOffDay: session.approvedOffDay,
  };
}

export async function getOpsMe(): Promise<OpsMe> {
  const me = await opsApiFetch<BackendOpsMe & { honorific?: 'bey' | 'hanim' | null }>('/ops/me');
  return {
    id: me.uid,
    firebaseUid: me.uid,
    email: me.email,
    displayName: me.displayName,
    honorific: me.honorific ?? null,
    role: me.role,
    title: 'Operasyon',
    currency: me.currency,
    active: true,
  };
}

export async function getContinuationVoiceMeta(): Promise<OpsContinuationVoiceMeta> {
  return opsApiFetch<OpsContinuationVoiceMeta>('/ops/continuation-voice/meta');
}

export async function fetchContinuationVoice(): Promise<OpsContinuationVoice> {
  return opsApiFetch<OpsContinuationVoice>('/ops/continuation-voice', { method: 'POST' });
}

/** Total of the server-side filtered set; fetches a single row so counts never depend on a page window. */
async function countOpsTasks(status: OpsTaskListStatus, signal?: AbortSignal): Promise<number> {
  const qs = new URLSearchParams({ limit: '1' });
  const backendStatus = apiTaskStatus(status);
  if (backendStatus) qs.set('status', backendStatus);
  const response = await opsApiFetch<ListStaffTasksResponse>(`/ops/tasks?${qs.toString()}`, signal ? { signal } : {});
  return response.total;
}

/** All-time earned / paid / balance, read independently of any period or task data. */
export async function getOpsDashboardBalance(signal?: AbortSignal): Promise<OpsDashboardBalance> {
  const summary = await opsApiFetch<OpsSummary>('/ops/summary', signal ? { signal } : {});
  return {
    currency: summary.currency,
    earned: money(summary.earnedMinor),
    paid: money(summary.paidMinor),
    balance: money(summary.balanceMinor),
  };
}

/**
 * Minutes, earnings and payments for explicit reporting periods. The reference instant is captured
 * once, so every returned period derives from the same moment and states its own bounds.
 */
export async function getOpsPeriodSummaries<K extends OpsReportingPeriodKey>(
  keys: readonly K[],
  options: { reference?: Date; signal?: AbortSignal } = {}
): Promise<Record<K, OpsPeriodSummary>> {
  const reference = options.reference ?? new Date();
  const periods = getOpsReportingPeriods(reference);
  const request = options.signal ? { signal: options.signal } : {};
  const summaries = await Promise.all(keys.map((key) => opsApiFetch<OpsSummary>(summaryPath(periods[key]), request)));
  return Object.fromEntries(
    keys.map((key, index) => {
      const summary = summaries[index]!;
      const result: OpsPeriodSummary = {
        period: periods[key],
        timeZone: OPS_REPORTING_TIME_ZONE,
        referenceAt: reference.toISOString(),
        minutes: minutes(summary.durationSeconds),
        earned: money(summary.earnedMinor),
        paid: money(summary.paidMinor),
        currency: summary.currency,
      };
      return [key, result];
    })
  ) as Record<K, OpsPeriodSummary>;
}

export async function getOpsDashboardTime(signal?: AbortSignal): Promise<OpsDashboardTimeSummary> {
  const { today, week } = await getOpsPeriodSummaries(['today', 'week'], { signal });
  return { referenceAt: today.referenceAt, timeZone: today.timeZone, today, week };
}

export async function getOpsDashboardMonth(signal?: AbortSignal): Promise<OpsPeriodSummary> {
  return (await getOpsPeriodSummaries(['month'], { signal })).month;
}

/** Totals of the full server-side filtered sets; independent of any preview page. */
export async function getOpsDashboardTaskCounts(signal?: AbortSignal): Promise<OpsDashboardTaskCounts> {
  const [open, completed] = await Promise.all([countOpsTasks('open', signal), countOpsTasks('done', signal)]);
  return { open, completed };
}

export async function getOpsOffDaysStatus(): Promise<OpsOffDaysStatus> {
  return opsApiFetch<OpsOffDaysStatus>('/ops/off-days/status');
}

export async function listOpsWeeklyOffDayRequests(
  params: ListOpsWeeklyOffDayRequestsParams = {}
): Promise<ListOpsWeeklyOffDayRequestsResponse> {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const query = qs.toString();
  return opsApiFetch<ListStaffWeeklyOffDayRequestsResponse>(`/ops/off-day-requests${query ? `?${query}` : ''}`);
}

export async function createOpsWeeklyOffDayRequest(
  body: CreateOpsWeeklyOffDayRequestInput
): Promise<OpsWeeklyOffDayRequest> {
  return opsApiFetch<StaffWeeklyOffDayRequest>('/ops/off-day-requests', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function cancelOpsWeeklyOffDayRequest(
  id: string,
  body: CancelOpsWeeklyOffDayRequestInput = {}
): Promise<OpsWeeklyOffDayRequest> {
  return opsApiFetch<StaffWeeklyOffDayRequest>(`/ops/off-day-requests/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function listOpsTasks(params: ListOpsTasksParams = {}): Promise<ListOpsTasksResponse> {
  const qs = new URLSearchParams();
  const backendStatus = apiTaskStatus(params.status);
  if (backendStatus) qs.set('status', backendStatus);
  if (params.q) qs.set('q', params.q);
  if (params.sort) qs.set('sort', params.sort);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const request = params.signal ? { signal: params.signal } : {};
  const [response, active] = await Promise.all([
    opsApiFetch<ListStaffTasksResponse>(`/ops/tasks${query ? `?${query}` : ''}`, request),
    params.withActiveSession === false
      ? Promise.resolve(null)
      : opsApiFetch<{ activeSession: StaffWorkSession | null }>('/ops/work-sessions/active', request),
  ]);
  return {
    ...response,
    items: response.items.map((task) => mapTask(task, active?.activeSession)),
  };
}

export async function getOpsTask(id: string): Promise<OpsTaskDetail> {
  const response = await opsApiFetch<StaffTaskDetail>(`/ops/tasks/${id}`);
  return {
    ...mapTask(response),
    acceptanceCriteria: null,
    attachments: null,
    comments: [],
    workLogs: response.sessions.map(mapWorkLog),
    reviewTimeline: response.reviewTimeline ?? [],
    personalNotes: response.personalNotes ?? [],
  };
}

export async function markOpsTaskRead(id: string): Promise<void> {
  await opsApiFetch(`/ops/tasks/${id}/read`, { method: 'POST' });
}

export async function markOpsTaskUnread(id: string): Promise<void> {
  await opsApiFetch(`/ops/tasks/${id}/unread`, { method: 'POST' });
}

export async function startOpsTimer(body: { taskId: string; note?: string }): Promise<OpsActiveTimer> {
  const response = await opsApiFetch<{ activeSession: StaffWorkSession }>('/ops/work-sessions/start', {
    method: 'POST',
    body: JSON.stringify({ taskId: body.taskId || null, subject: body.note?.trim() || 'Çalışma' }),
  });
  const timer = mapTimer(response.activeSession);
  if (!timer) throw new Error('Çalışma başlatılamadı');
  return timer;
}

export async function updateOpsTimerTask(body: { timerId: string; taskId: string }): Promise<OpsActiveTimer> {
  const response = await opsApiFetch<{ activeSession: StaffWorkSession }>(`/ops/work-sessions/${body.timerId}/task`, {
    method: 'PATCH',
    body: JSON.stringify({ taskId: body.taskId || null }),
  });
  const timer = mapTimer(response.activeSession);
  if (!timer) throw new Error('Görev güncellenemedi');
  return timer;
}

export async function finishOpsTimer(body: { timerId: string; note?: string }): Promise<OpsWorkLog> {
  const response = await opsApiFetch<StaffWorkSession>(`/ops/work-sessions/${body.timerId}/finish`, {
    method: 'POST',
    body: JSON.stringify({ note: body.note }),
  });
  return mapWorkLog(response);
}

/** Authoritative read of the staff member's running timer; null means the server confirms none is running. */
export async function getOpsActiveTimer(signal?: AbortSignal): Promise<OpsActiveTimer | null> {
  const response = await opsApiFetch<{ activeSession: StaffWorkSession | null }>(
    '/ops/work-sessions/active',
    signal ? { signal } : {}
  );
  return mapTimer(response.activeSession);
}

export async function getOpsHeartbeat(): Promise<OpsHeartbeat> {
  const response = await opsApiFetch<OpsHeartbeatResponse>('/ops/work-sessions/heartbeat');
  return {
    serverNow: response.serverNow,
    activeTimer: mapTimer(response.activeSession),
    continuationPrompt: response.continuationPrompt,
    autoFinishedLog: response.autoFinishedSession ? mapWorkLog(response.autoFinishedSession) : null,
  };
}

export async function confirmOpsContinuation(body: { timerId: string; checkInId: string }): Promise<OpsActiveTimer> {
  const response = await opsApiFetch<{ activeSession: StaffWorkSession }>(
    `/ops/work-sessions/${body.timerId}/check-ins/${body.checkInId}/confirm`,
    { method: 'POST' }
  );
  const timer = mapTimer(response.activeSession);
  if (!timer) throw new Error('Devam kontrolü onaylanamadı');
  return timer;
}

export async function sendOpsTaskToReview(taskId: string, message: string): Promise<OpsTaskListItem> {
  const response = await opsApiFetch<StaffTaskListItem>(`/ops/tasks/${taskId}/send-review`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
  return mapTask(response);
}

export async function sendOpsTaskManagerRequest(taskId: string, message: string): Promise<OpsTaskListItem> {
  const response = await opsApiFetch<StaffTaskListItem>(`/ops/tasks/${taskId}/manager-request`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
  return mapTask(response);
}

export async function requestOpsTaskCancellation(taskId: string, reason: string): Promise<void> {
  await opsApiFetch(`/ops/tasks/${taskId}/cancel-request`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function updateOpsTaskPersonalNote(taskId: string, body: string): Promise<OpsTaskDetail> {
  const response = await opsApiFetch<StaffTaskDetail>(`/ops/tasks/${taskId}/personal-note`, {
    method: 'PUT',
    body: JSON.stringify({ body }),
  });
  return {
    ...mapTask(response),
    acceptanceCriteria: null,
    attachments: null,
    comments: [],
    workLogs: response.sessions.map(mapWorkLog),
    reviewTimeline: response.reviewTimeline ?? [],
    personalNotes: response.personalNotes ?? [],
  };
}

export async function sendOpsActivity(body: { timerId: string; visible: boolean; focused: boolean; clientNow: string }): Promise<OpsActiveTimer | null> {
  const response = await opsApiFetch<{ activeSession: StaffWorkSession | null; nextCheckAt: string | null }>(
    `/ops/work-sessions/${body.timerId}/activity`,
    {
      method: 'POST',
      body: JSON.stringify({ visible: body.visible, focused: body.focused, clientNow: body.clientNow }),
    }
  );
  return mapTimer(response.activeSession);
}

/** All-time earnings position from the authoritative summary, independent of any history page. */
export async function getOpsEarningsSummary(signal?: AbortSignal): Promise<OpsEarningsSummary> {
  const summary = await opsApiFetch<OpsSummary>('/ops/summary', signal ? { signal } : {});
  return {
    currency: summary.currency,
    pending: money(summary.balanceMinor),
    approved: money(summary.earnedMinor),
    paid: money(summary.paidMinor),
    totalMinutes: minutes(summary.durationSeconds),
    regularMinutes: minutes(summary.regularDurationSeconds),
    approvedOffDayMinutes: minutes(summary.approvedOffDayDurationSeconds),
    regularEarnings: money(summary.regularEarnedMinor),
    approvedOffDayEarnings: money(summary.approvedOffDayEarnedMinor),
    periodLabel: 'Tüm zamanlar',
  };
}

export interface ListOpsHistoryParams {
  limit: number;
  offset: number;
  signal?: AbortSignal;
}

function historyQuery({ limit, offset }: ListOpsHistoryParams): string {
  return new URLSearchParams({ limit: String(limit), offset: String(offset) }).toString();
}

/** One page of finished work sessions, newest first. A still-running session is not an earnings record. */
export async function listOpsEarningsWorkLogs(params: ListOpsHistoryParams): Promise<OpsHistoryPage<OpsWorkLog>> {
  const response = await opsApiFetch<ListStaffWorkSessionsResponse>(
    `/ops/work-sessions?${historyQuery(params)}`,
    params.signal ? { signal: params.signal } : {}
  );
  return {
    items: response.items.filter((session) => session.status !== 'active').map(mapWorkLog),
    offset: response.offset,
    limit: response.limit,
    total: response.total,
    fetched: response.items.length,
  };
}

/** One page of recorded payments, newest payment date first. */
export async function listOpsEarningsPayments(params: ListOpsHistoryParams): Promise<OpsHistoryPage<OpsPayment>> {
  const response = await opsApiFetch<ListStaffPaymentsResponse>(
    `/ops/payments?${historyQuery(params)}`,
    params.signal ? { signal: params.signal } : {}
  );
  return {
    items: response.items.map((payment) => ({
      id: payment.id,
      amount: money(payment.amountMinor),
      currency: payment.currency,
      method: payment.method,
      issuedAt: payment.createdAt,
      paidAt: payment.paidAt,
      note: payment.note,
    })),
    offset: response.offset,
    limit: response.limit,
    total: response.total,
    fetched: response.items.length,
  };
}
